"use client";

import { useCallback, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { parseAsString } from "nuqs";
import { ArchiveConfirmModal } from "@/components/ui/archive-confirm-modal";
import { showToast } from "@/components/ui/sonner";
import { useQuerySearchParams } from "@/hooks/use-query-search-params";
import { useArchiveProject, useGetProject } from "@/lib/api/generated";
import {
  getPermissionErrorMessage,
  isPermissionDenied,
} from "@/lib/permissions";

const ARCHIVE_PROJECT_PARAM = {
  field: "archive_project",
  parser: parseAsString.withDefault(""),
} as const;

export function useOpenArchiveProject() {
  const [, setParam] = useQuerySearchParams(ARCHIVE_PROJECT_PARAM);
  return useCallback(
    (id: string) => {
      setParam(id, { history: "replace", scroll: false });
    },
    [setParam],
  );
}

export function ProjectsArchiveConfirmModal() {
  const queryClient = useQueryClient();
  const [projectId, setProjectId] = useQuerySearchParams(ARCHIVE_PROJECT_PARAM);
  const open = !!projectId?.trim();

  const clearParam = useCallback(() => {
    setProjectId(null, { history: "replace", scroll: false });
  }, [setProjectId]);

  const { data: project, isPending, isError } = useGetProject(projectId ?? "", {
    query: {
      enabled: open,
      retry: false,
    },
  });

  useEffect(() => {
    if (!open || !isError) return;
    showToast.error("Could not load project", {
      description: "Try again from the project list.",
    });
    clearParam();
  }, [open, isError, clearParam]);

  const archiveMutation = useArchiveProject(
    {
      mutation: {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["projects"] });
          queryClient.invalidateQueries({ queryKey: ["jobs"] });
          clearParam();
          showToast.success("Project archived");
        },
        onError: (err: Error) => {
          if (isPermissionDenied(err)) {
            queryClient.invalidateQueries({ queryKey: ["projects"] });
            showToast.error("Permission denied", {
              description: getPermissionErrorMessage(err),
            });
          } else {
            showToast.error("Archive failed", { description: err.message });
          }
        },
      },
    },
    queryClient,
  );

  const handleConfirm = useCallback(() => {
    if (projectId) {
      archiveMutation.mutate({ projectId });
    }
  }, [archiveMutation, projectId]);

  return (
    <ArchiveConfirmModal
      open={open}
      onClose={clearParam}
      onConfirm={handleConfirm}
      entityType="project"
      entityName={project?.title ?? ""}
      detailsLoading={open && isPending}
      loading={archiveMutation.isPending}
      description="Archiving this project removes it from active views and clears it from selection if it is currently active."
    />
  );
}
